import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { MaterialIcon } from "@/components/MaterialIcon";
import { useApi } from "@/lib/useApi";
import { useAuth } from "@/context/AuthContext";
import { errMsg, resolveUrl } from "@/lib/api";

const inputCls =
  "w-full h-[54px] rounded-full border border-[#CFCFCF] bg-[#FAFAFA] px-6 text-[17px] font-medium outline-none placeholder:text-[#B5B5B5] focus:border-[#111] transition-colors";

const SideCard = ({ video, side }) => (
  <div
    className={`absolute top-[12%] bottom-[12%] w-[22%] overflow-hidden rounded-2xl bg-[#D9D9D9] ${side === "left" ? "left-0" : "right-0"}`}
    aria-hidden="true"
  >
    {video?.thumbnail_url && <img src={resolveUrl(video.thumbnail_url)} alt="" className="h-full w-full object-cover blur-[2px] opacity-60" />}
  </div>
);

const Hero = ({ videos }) => {
  const [a, b, c] = videos;
  return (
    <div className="relative mt-12 w-full" data-testid="organizer-hero">
      <div className="relative mx-auto aspect-[16/9] w-full max-w-[860px]">
        <SideCard video={b} side="left" />
        <SideCard video={c} side="right" />
        <div className="absolute inset-x-[12%] inset-y-0 overflow-hidden rounded-2xl bg-black shadow-[0_24px_60px_rgba(0,0,0,0.22)]">
          {a?.thumbnail_url && <img src={resolveUrl(a.thumbnail_url)} alt="" className="h-full w-full object-cover" />}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-[78px] w-[78px] items-center justify-center rounded-full bg-white/90 shadow-lg">
              <MaterialIcon name="play_arrow" filled size={46} className="ml-1 text-black" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function OrganizerLanding() {
  const { data: home } = useApi("/home");
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", organizer_for: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const videos = (home?.rows || []).flatMap((r) => r.videos || []).slice(0, 3);
  const ready = form.email.trim() && form.password.length >= 8 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError("");
    try {
      await register(form.email.trim(), form.password, form.organizer_for.trim());
      toast.success("Welcome to Dancerfloor");
      navigate("/admin");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F2]" data-testid="organizer-landing">
      <div className="mx-auto max-w-[980px] px-6 pb-24 pt-14 text-center">
        <Logo size={40} />
        <h1 className="mx-auto mt-10 max-w-[760px] text-[30px] font-bold leading-[1.15] sm:text-[40px] lg:text-[46px]" data-testid="organizer-headline">
          Turn dance videos into ticket and merch sales.
        </h1>
        <p className="mx-auto mt-5 max-w-[620px] text-[15px] text-[#5C5C5C] md:text-lg" data-testid="organizer-subheadline">
          Showcase your festival. Let dancers shop from every video.
        </p>

        <Hero videos={videos} />

        <button
          type="button"
          onClick={() => document.getElementById("organizer-signup")?.scrollIntoView({ behavior: "smooth", block: "center" })}
          className="mt-14 w-full max-w-[560px] rounded-lg bg-[#F8D481] px-8 py-5 text-[15px] font-bold uppercase tracking-wide transition-colors hover:bg-[#f3c968] md:text-lg"
          data-testid="organizer-cta-button"
        >
          Upgrade Your Festival
        </button>

        <form
          id="organizer-signup"
          onSubmit={submit}
          className="mx-auto mt-16 w-full max-w-[520px] rounded-[24px] border border-[#E2E2DE] bg-white px-7 py-10 text-left shadow-[0_18px_50px_rgba(0,0,0,0.06)] scroll-mt-24"
          data-testid="organizer-signup-form"
        >
          <p className="text-center text-[22px] font-bold">Create your organizer account</p>
          <div className="mt-8 space-y-4">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email"
              autoComplete="email"
              className={inputCls}
              data-testid="organizer-email-input"
            />
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Password (8+ characters)"
              autoComplete="new-password"
              className={inputCls}
              data-testid="organizer-password-input"
            />
            <input
              value={form.organizer_for}
              onChange={(e) => setForm({ ...form, organizer_for: e.target.value })}
              placeholder="Event Organizer For…"
              className={inputCls}
              data-testid="organizer-for-input"
            />
          </div>
          {error && (
            <p className="mt-4 text-center text-[14px] font-medium text-[#B91C1C]" data-testid="organizer-signup-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={!ready}
            className={`mt-7 h-[54px] w-full rounded-full text-[18px] font-semibold transition-colors ${
              ready ? "bg-[#111] text-white hover:bg-[#2b2b2b]" : "cursor-not-allowed bg-[#EDEDEB] text-[#8A8A8A]"
            }`}
            data-testid="organizer-signup-submit"
          >
            {busy ? "Creating account…" : "Get Started"}
          </button>
          <p className="mt-5 text-center text-[14px] text-[#8A8A8A]">
            Already an organizer?{" "}
            <button type="button" className="font-semibold text-[#111] hover:underline" onClick={() => navigate("/login")} data-testid="organizer-login-link">
              Log in
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
